import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import { Button } from "@/components/button";
import { useNavigate } from "react-router-dom";
import type { IExamProgress } from "@/apis/exam_progress/types";
import { Badge } from "@/components/badge";
import { getPaperTypeLabel } from "@/constants/exam";

interface ExamsTableProps {
  exams: IExamProgress[];
  isLoading: boolean;
  error: unknown;
  onStartPaper: (
    progressId: string,
    paperId: string,
    paperType: string
  ) => void;
}

// 날짜 포맷 함수
const formatDate = (dateString?: string, showTime: boolean = false) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    ...(showTime && {
      hour: "2-digit",
      minute: "2-digit",
    }),
  });
};

const getSubjectScore = (subjects: any[] | undefined, subjectType: string) => {
  if (!subjects) return null;
  const subject = subjects.find((s) => s.subject === subjectType);
  return subject?.total_score;
};

const getShortPaperTypeLabel = (subject: string): string => {
  if (subject === "reading_writing") {
    return "R&W";
  }
  return getPaperTypeLabel(subject);
};



// 모듈 상태 표시 함수
const getModuleStatusDisplay = (moduleProgress: any[]) => {
  if (!moduleProgress || moduleProgress.length === 0) {
    return "Not Started";
  }

  return moduleProgress.map((module, index) => (
    <div key={index} className="text-xs">
      Module {module.module_no}:{" "}
      {module.status === "not_started"
        ? "Ready"
        : module.status === "in_progress"
        ? "In Progress"
        : "Completed"}
    </div>
  ));
};

const ExamsTable: React.FC<ExamsTableProps> = ({
  exams,
  isLoading,
  error,
  onStartPaper,
}) => {
  const navigate = useNavigate();

  if (isLoading) {
    return <div className="text-center py-4">Loading your exams...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-4 text-red-500">
        Error loading exams. Please try again.
      </div>
    );
  }

  if (exams.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No exams assigned yet.
      </div>
    );
  }

  return (
    <Table className="mt-4">
      <TableHead>
        <TableRow>
          <TableHeader>Exam Title</TableHeader>
          <TableHeader>Status</TableHeader>
          <TableHeader>Started At</TableHeader>
          <TableHeader>Test Sections</TableHeader>
          <TableHeader>Score</TableHeader>
          <TableHeader>Report</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {exams.map((progress) => (
          <TableRow key={progress.id} className="hover:bg-zinc-50">
            <TableCell className="font-medium">
              <div className="flex items-center gap-2">
                <span>{progress?.exam_set?.title || "N/A"}</span>
                {progress.is_retake && (
                  <Badge color="amber" className="text-xs">
                    Retake
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell>
              {progress.status === "not_started" && (
                <Badge color="zinc">Not Started</Badge>
              )}
              {progress.status === "in_progress" && (
                <Badge color="yellow">In Progress</Badge>
              )}
              {progress.status === "completed" && (
                <Badge color="blue">Completed</Badge>
              )}
            </TableCell>
            <TableCell>{formatDate(progress.started_at, true)}</TableCell>
            <TableCell>
              {progress.assigned_papers &&
              progress.assigned_papers.length > 0 ? (
                <div className="space-y-2">
                  {progress.assigned_papers.map((paper, index) => {
                    const moduleScore = progress.exam_report?.subjects 
                      ? getSubjectScore(progress.exam_report.subjects, paper.subject) 
                      : null;

                    return (
                      <div
                        key={paper.exam_paper_id}
                        className="flex items-center gap-2"
                      >
                        <Badge color="zinc" className="text-xs">
                          {getShortPaperTypeLabel(paper.subject)}
                        </Badge>
                        {paper.status === "not_started" ? (
                          <Button
                            className="text-xs px-2 py-1"
                            onClick={() =>
                              onStartPaper(
                                progress.id,
                                paper.exam_paper_id,
                                paper.subject
                              )
                            }
                          >
                            Start
                          </Button>
                        ) : paper.status === "in_progress" ? (
                          <Button
                            className="text-xs px-2 py-1"
                            onClick={() =>
                              navigate(
                                `/my_exams/${progress.id}/exam_paper/${paper.exam_paper_id}/take`
                              )
                            }
                          >
                            Continue
                          </Button>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-gray-900">
                              {moduleScore !== null
                                ? `${moduleScore}`
                                : "Completed"}
                            </span>
                            {paper.status === "completed" && (
                              <Button
                                outline
                                className="!text-xs !px-2 !py-1.5 ml-1 rounded-base"
                                onClick={() =>
                                  navigate(
                                    `/exam_results/view/${progress.id}/${paper.exam_paper_id}`
                                  )
                                }
                              >
                                View
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="text-gray-500">No papers assigned</span>
              )}
            </TableCell>
            <TableCell>
              <span className="font-medium text-lg text-gray-900">
                {progress.exam_report?.total_score || "-"}
              </span>
            </TableCell>
            <TableCell>
              {progress.exam_report ? (
                <Button
                  outline
                  className="text-sm px-3 py-1"
                  onClick={() =>
                    navigate(`/exam_progress/${progress.id}/report`)
                  }
                >
                  Report
                </Button>
              ) : null}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default ExamsTable;
